import React, { useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import Globe from './Globe';
import { updateTrip, requestTripHighlight } from '../data/firestore';
import { geoSearch, getAssetPlaybackUrl } from '../data/uploadService';

// Precise, video-scrubbing editor for the trip's hover-preview segment AND the
// static poster frame that loads on the tile. Frame-step, fine scrub, and
// "set from current frame" give exact control over both.
function HighlightEditor({ asset, value, onChange }) {
  const vref = useRef(null);
  const [url, setUrl] = useState('');
  const [dur, setDur] = useState(0);
  const [cursor, setCursor] = useState(value.inSec || 0);
  const stopAt = useRef(null);
  const FPS = 30;

  useEffect(() => {
    let on = true;
    setUrl('');
    if (asset?.s3Key) getAssetPlaybackUrl(asset.s3Key).then((u) => on && setUrl(u)).catch(() => {});
    return () => {
      on = false;
    };
  }, [asset?.s3Key]);

  const len = Math.max(0, (value.outSec || 0) - (value.inSec || 0)) || 6;

  const seek = (t) => {
    const c = Math.max(0, Math.min(t, dur || t));
    setCursor(c);
    const v = vref.current;
    if (v) {
      stopAt.current = null;
      v.pause();
      v.currentTime = c;
    }
  };

  const fmt = (s) => `${(s || 0).toFixed(2)}s`;

  const onTime = () => {
    const v = vref.current;
    if (!v) return;
    setCursor(v.currentTime);
    if (stopAt.current != null && v.currentTime >= stopAt.current) {
      v.pause();
      stopAt.current = null;
    }
  };

  const previewSegment = () => {
    const v = vref.current;
    if (!v) return;
    v.currentTime = value.inSec || 0;
    stopAt.current = (value.inSec || 0) + len;
    v.play().catch(() => {});
  };

  return (
    <div>
      <div className="cs-hl-preview">
        {url ? (
          <video
            ref={vref}
            src={url}
            muted
            playsInline
            preload="auto"
            onLoadedMetadata={(e) => {
              setDur(e.target.duration || 0);
              e.target.currentTime = value.inSec || 0;
            }}
            onTimeUpdate={onTime}
            style={{ width: '100%', display: 'block', borderRadius: 8, background: '#000' }}
          />
        ) : (
          <div className="cs-empty">Loading clip…</div>
        )}
      </div>

      <input
        className="cs-slider"
        type="range"
        min="0"
        max={dur || 1}
        step="0.01"
        value={Math.min(cursor, dur || cursor)}
        onChange={(e) => seek(Number(e.target.value))}
        style={{ marginTop: 10 }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => seek(cursor - 1)}>−1s</button>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => seek(cursor - 1 / FPS)}>◀ frame</button>
        <span style={{ fontFamily: 'monospace', fontSize: 13, minWidth: 96, textAlign: 'center' }}>
          {fmt(cursor)} / {fmt(dur)}
        </span>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => seek(cursor + 1 / FPS)}>frame ▶</button>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => seek(cursor + 1)}>+1s</button>
        <input
          className="cs-input cs-input-sm"
          type="number"
          step="0.01"
          min="0"
          value={Number(cursor.toFixed(2))}
          onChange={(e) => seek(Number(e.target.value))}
          style={{ width: 90, marginLeft: 'auto' }}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        <button
          type="button"
          className="cs-btn cs-btn-primary cs-btn-sm"
          onClick={() => onChange({ ...value, inSec: cursor, outSec: cursor + len })}
        >
          ▶ Set segment start here
        </button>
        <button
          type="button"
          className="cs-btn cs-btn-primary cs-btn-sm"
          onClick={() => onChange({ ...value, posterSec: cursor })}
        >
          🖼 Set poster frame here
        </button>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={previewSegment}>
          Preview segment
        </button>
      </div>

      <div className="cs-hl-stats">
        <div>
          <span className="lbl">Segment start</span>
          <span className="val">{fmt(value.inSec)}</span>
          <button type="button" className="cs-link" onClick={() => seek(value.inSec || 0)}>go</button>
        </div>
        <div>
          <span className="lbl">Length</span>
          <input
            className="cs-input cs-input-sm"
            type="number"
            min="1"
            max="8"
            step="0.5"
            value={Number(len.toFixed(1))}
            onChange={(e) => onChange({ ...value, outSec: (value.inSec || 0) + Number(e.target.value) })}
            style={{ width: 70 }}
          />
          <span className="cs-help" style={{ marginLeft: 6 }}>sec (max 8)</span>
        </div>
        <div>
          <span className="lbl">Poster frame</span>
          <span className="val">{value.posterSec == null ? `${fmt(value.inSec)} (start)` : fmt(value.posterSec)}</span>
          <button type="button" className="cs-link" onClick={() => seek(value.posterSec ?? value.inSec ?? 0)}>go</button>
        </div>
      </div>
    </div>
  );
}

// Edit trip metadata, place its globe marker (click the globe, type coordinates,
// or locate by name), and pick the hover-preview highlight clip.
export default function TripEditModal({ trip, videos = [], onClose }) {
  const [form, setForm] = useState({
    name: trip.name || '',
    location: trip.location || '',
    startDate: trip.startDate || '',
    endDate: trip.endDate || '',
    description: trip.description || '',
    lat: trip.lat == null ? '' : trip.lat,
    lng: trip.lng == null ? '' : trip.lng,
    highlightAssetId: trip.highlightAssetId || '',
    highlightInSec: trip.highlightInSec || 0,
    highlightOutSec: trip.highlightOutSec || 0,
    highlightPosterSec: trip.highlightPosterSec == null ? null : trip.highlightPosterSec,
  });
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  const [geoErr, setGeoErr] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const latNum = Number(form.lat);
  const lngNum = Number(form.lng);
  const hasLoc = Number.isFinite(latNum) && form.lat !== '' && Number.isFinite(lngNum) && form.lng !== '';

  const locate = async () => {
    setLocating(true);
    setGeoErr(null);
    try {
      const results = await geoSearch(form.location || form.name);
      if (results.length) {
        setForm((f) => ({ ...f, lat: results[0].lat, lng: results[0].lng }));
      } else {
        setGeoErr('No match — click the globe to place the marker manually.');
      }
    } catch (err) {
      setGeoErr(err.message || String(err));
    } finally {
      setLocating(false);
    }
  };

  // Did anything that affects the rendered poster/preview change?
  const highlightChanged = () =>
    (form.highlightAssetId || '') !== (trip.highlightAssetId || '') ||
    (Number(form.highlightInSec) || 0) !== (trip.highlightInSec || 0) ||
    (Number(form.highlightOutSec) || 0) !== (trip.highlightOutSec || 0) ||
    (form.highlightPosterSec == null ? null : Number(form.highlightPosterSec)) !==
      (trip.highlightPosterSec == null ? null : trip.highlightPosterSec);

  const save = async (forceRender) => {
    setBusy(true);
    try {
      const patch = {
        name: form.name,
        location: form.location,
        startDate: form.startDate,
        endDate: form.endDate,
        description: form.description,
        lat: hasLoc ? latNum : null,
        lng: hasLoc ? lngNum : null,
        highlightAssetId: form.highlightAssetId || '',
        highlightInSec: Number(form.highlightInSec) || 0,
        highlightOutSec: Number(form.highlightOutSec) || 0,
        highlightPosterSec: form.highlightPosterSec == null ? null : Number(form.highlightPosterSec),
      };
      await updateTrip(trip.id, patch);
      // Auto-render whenever the highlight selection changed (or no preview
      // exists yet) so the saved config actually shows on the homepage.
      const needsRender =
        !!form.highlightAssetId &&
        (forceRender || highlightChanged() || !trip.highlightPreviewUrl);
      if (needsRender) {
        await requestTripHighlight(trip.id);
      }
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Edit trip" onClose={busy ? () => {} : onClose} maxWidth={760}>
      <div className="cs-row-2" style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 20 }}>
        <div>
          <Globe
            lat={hasLoc ? latNum : NaN}
            lng={hasLoc ? lngNum : NaN}
            size={220}
            dot={hasLoc}
            onPick={({ lat, lng }) => setForm((f) => ({ ...f, lat, lng }))}
          />
          <div className="cs-help" style={{ textAlign: 'center' }}>Click the globe to set the marker</div>
        </div>
        <div>
          <div className="cs-field" style={{ margin: 0 }}>
            <label>Trip name</label>
            <input className="cs-input" value={form.name} onChange={set('name')} />
          </div>
          <div className="cs-field">
            <label>Location</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="cs-input" value={form.location} onChange={set('location')} />
              <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={locating} onClick={locate}>
                {locating ? '…' : 'Locate'}
              </button>
            </div>
            {geoErr && <div className="cs-help" style={{ color: '#e88' }}>{geoErr}</div>}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="cs-field" style={{ flex: 1 }}>
              <label>Latitude</label>
              <input className="cs-input" type="number" value={form.lat} onChange={set('lat')} />
            </div>
            <div className="cs-field" style={{ flex: 1 }}>
              <label>Longitude</label>
              <input className="cs-input" type="number" value={form.lng} onChange={set('lng')} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="cs-field" style={{ flex: 1 }}>
              <label>Start date</label>
              <input className="cs-input" type="date" value={form.startDate} onChange={set('startDate')} />
            </div>
            <div className="cs-field" style={{ flex: 1 }}>
              <label>End date</label>
              <input className="cs-input" type="date" value={form.endDate} onChange={set('endDate')} />
            </div>
          </div>
          <div className="cs-field">
            <label>Description</label>
            <input className="cs-input" value={form.description} onChange={set('description')} />
          </div>
        </div>
      </div>

      <div style={{ borderTop: '1px solid #2a2a33', marginTop: 8, paddingTop: 16 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>Hover-preview highlight</h3>
        <p className="cs-help" style={{ marginTop: 0 }}>
          Pick a clip and a short window — the homepage tile plays a muted preview on hover.
        </p>
        <div className="cs-field">
          <label>Highlight clip</label>
          <select className="cs-input" value={form.highlightAssetId} onChange={set('highlightAssetId')}>
            <option value="">— none —</option>
            {videos.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        </div>
        {form.highlightAssetId && (
          <HighlightEditor
            asset={videos.find((v) => v.id === form.highlightAssetId)}
            value={{
              inSec: Number(form.highlightInSec) || 0,
              outSec: Number(form.highlightOutSec) || 0,
              posterSec: form.highlightPosterSec,
            }}
            onChange={(v) =>
              setForm((f) => ({
                ...f,
                highlightInSec: v.inSec,
                highlightOutSec: v.outSec,
                highlightPosterSec: v.posterSec == null ? null : v.posterSec,
              }))
            }
          />
        )}
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 16, alignItems: 'center' }}>
        <button className="cs-btn cs-btn-primary" disabled={busy || !form.name} onClick={() => save(false)}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button className="cs-btn cs-btn-ghost" disabled={busy || !form.highlightAssetId} onClick={() => save(true)}>
          Save & re-render highlight
        </button>
        {form.highlightAssetId && (
          <span className="cs-help">
            Saving re-renders the poster + preview when the clip/segment/poster changed (≈30–60s).
          </span>
        )}
      </div>
    </Modal>
  );
}
